import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { distinctUntilChanged } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject, Observable } from 'rxjs';

import { ApiService, TokenService } from '../../api';
import { Environment } from '../../interfaces';
import { EnvironmentService } from '../../services/environment.service';
import { HttpClient } from '@angular/common/http';
import { TokenResponse } from '../../api/interfaces/token.response';
import { LoginBase } from '../models';
import { AuthenticationUserResponse } from '../interfaces/authentication-user.response';

@Injectable()
export abstract class AuthenticationService<T = AuthenticationUserResponse> {
  /**
   * The current, loged in user.
   *
   * @type {(T | null)}
   * @memberof UserAuthenticationService
   */
  public currentUser: Observable<T>;
  public currentUserResponse: Observable<T>;

  /**
   * Defines if the client is authenticated.
   *
   * @type {Observable<boolean>}
   * @memberof UserAuthenticationService
   */
  public isAuthenticated: Observable<boolean>;

  /**
   * Internal user subject.
   *
   * @protected
   * @memberof UserAuthenticationService
   */
  public currentUserSubject = new BehaviorSubject<T>(undefined);
  public currentUserResponseSubject = new BehaviorSubject<T>(undefined);

  constructor(
    protected readonly environmentService: EnvironmentService,
    protected readonly tokenService: TokenService,
    protected readonly apiService: ApiService,
    protected readonly router: Router,
    protected readonly http: HttpClient
  ) {
    this.environment = this.environmentService.environment;

    this.isAuthenticated = this.isAuthenticatedSubject.asObservable();
    this.currentUser = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());
    this.currentUserResponse = this.currentUserResponseSubject.asObservable().pipe(distinctUntilChanged());

    this.tokenService.nearlyExpired.subscribe((token) => {
      this.refreshAccessToken(token).subscribe(null, (err) => {
        this.purgeAuth(true);
        console.error(err);
      });
    });
  }

  /**
   * Redirect Uri.
   *
   * @protected
   * @memberof AuthenticationService
   */
  protected redirectUri = '/auth/login';

  /**
   * Internal authentication flag.
   *
   * @protected
   * @memberof UserAuthenticationService
   */
  protected readonly isAuthenticatedSubject = new ReplaySubject<boolean>(1);

  /**
   * Environment from the CoreModule.
   *
   * @protected
   * @type {Environment}
   * @memberof UserAuthenticationService
   */
  protected readonly environment: Environment;

  /**
   * Headers.
   *
   * @protected
   * @memberof UserAuthenticationService
   */
  protected readonly header = {};

  /**
   * This runs once on application startup.
   *
   * @param {boolean} [navigation=true] Defines, if you would like to navigate to login.
   * @returns {Observable<boolean>}
   * @memberof UserAuthenticationService
   */
  public initialize(navigation: boolean = true, params?: Object): void {
    if (this.tokenService.hasToken()) {
      this.apiService.getOne<T>('/me', params).subscribe(
        (res) => {
          this.setAuth(res);
          this.currentUserResponseSubject.next(res);
          return true;
        },
        (err) => {
          console.error(err);
          this.purgeAuth(navigation);
          throw err;
        }
      );
    } else {
      this.purgeAuth(navigation);
    }
  }

  /**
   * This function does the logout. Removes the tokens, and removes the user.
   *
   * @param {boolean} [navigation=true]
   * @memberof UserAuthenticationService
   */
  public purgeAuth(navigation: boolean = true) {
    this.currentUserSubject.next(undefined);
    this.currentUserResponseSubject.next(undefined);
    this.isAuthenticatedSubject.next(false);

    this.tokenService.deleteToken();

    if (navigation) {
      this.router.navigate([this.redirectUri]);
    }
  }

  /**
   * This function registrate one user to the API.
   *
   * @abstract
   * @param {{
   *     firstName: string;
   *     lastName: string;
   *     email: string;
   *     password: string;
   *     passwordConfirmation: string;
   *   }} user
   * @returns {Observable<boolean>}
   * @memberof UserAuthenticationService
   */
  public abstract registrateUser(user: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    passwordConfirmation: string;
  }): Observable<boolean>;

  /**
   * This function gains the Tokens from the API.
   *
   * @abstract
   * @param {LoginBase} loginData
   * @returns {Observable<boolean>}
   * @memberof UserAuthenticationService
   */
  public abstract obtainAccessToken(loginData: LoginBase): Observable<boolean>;

  /**
   * This function resets the Access token.
   *
   * @protected
   * @abstract
   * @param {string} token
   * @returns {Observable<boolean>}
   * @memberof UserAuthenticationService
   */
  protected abstract refreshAccessToken(token: string): Observable<boolean>;

  /**
   * This function handles the actions after a successful authentication.
   *
   * @protected
   * @param {boolean} res
   * @returns
   * @memberof UserAuthenticationService
   */
  protected handleSuccess(res: TokenResponse): boolean {
    this.tokenService.setToken(res);
    this.initialize();
    return true;
  }

  /**
   * This method stores the tokens, and fires the logged in events.
   *
   * @protected
   * @param {T} user
   * @memberof UserAuthenticationService
   */
  protected setAuth(user: T) {
    // Set isAuthenticated to true
    this.isAuthenticatedSubject.next(true);

    // Set current user data into observable
    this.currentUserSubject.next(user);
  }
}
